import { Drawer, Descriptions, Card, Table, Tag, Space, Typography, Empty, Spin } from 'antd'
import {
  MailOutlined,
  TwitterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons'
import { useProject } from '../hooks'
import type { ScrapeStatus } from '../types'
import { formatDateTime } from '../utils/format'

const { Text, Link } = Typography

interface ProjectInfoDrawerProps {
  projectId: string | null
  visible: boolean
  onClose: () => void
}

const scrapeStatusConfig: Record<ScrapeStatus, { color: string; text: string; icon: JSX.Element }> = {
  NOT_SCRAPED: {
    color: 'default',
    text: '未抓取',
    icon: <MinusCircleOutlined />,
  },
  SCRAPED_NO_DATA: {
    color: 'warning',
    text: '无数据',
    icon: <CloseCircleOutlined />,
  },
  SCRAPED_SUCCESS: {
    color: 'success',
    text: '已抓取',
    icon: <CheckCircleOutlined />,
  },
}

const ProjectInfoDrawer: React.FC<ProjectInfoDrawerProps> = ({ projectId, visible, onClose }) => {
  const { data: project, isLoading } = useProject(projectId || '', {
    enabled: !!projectId && visible,
  })

  if (!projectId) {
    return null
  }

  const adminColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (username: string) => (username ? `@${username}` : '-'),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const roleMap: Record<string, { text: string; color: string }> = {
          creator: { text: '创建者', color: 'purple' },
          admin: { text: '管理员', color: 'blue' },
          member: { text: '成员', color: 'default' },
        }
        const config = roleMap[role] || { text: role, color: 'default' }
        return <Tag color={config.color}>{config.text}</Tag>
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a: any, b: any) => a.priority - b.priority,
    },
    {
      title: '活跃度',
      dataIndex: 'activity_score',
      key: 'activity_score',
      render: (score: number) => (score ? `${score}分` : '-'),
      sorter: (a: any, b: any) => (a.activity_score || 0) - (b.activity_score || 0),
    },
  ]

  return (
    <Drawer
      title="项目详情"
      width={800}
      open={visible}
      onClose={onClose}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      ) : project ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 基本信息 */}
          <Card title="基本信息" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="项目名称">{project.name}</Descriptions.Item>
              <Descriptions.Item label="网站">
                <Link href={project.website} target="_blank">
                  {project.website}
                </Link>
              </Descriptions.Item>
              {project.category && (
                <Descriptions.Item label="类别">
                  <Tag>{project.category}</Tag>
                </Descriptions.Item>
              )}
              {project.description && (
                <Descriptions.Item label="描述">{project.description}</Descriptions.Item>
              )}
              {project.priority_score && (
                <Descriptions.Item label="优先级评分">{project.priority_score}</Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* 联系信息 */}
          <Card title="联系信息" size="small">
            <Descriptions column={1} size="small">
              {/* 邮箱 */}
              <Descriptions.Item label="邮箱">
                {project.email ? (
                  <Space direction="vertical" size={0}>
                    <Space>
                      <MailOutlined />
                      <Link href={`mailto:${project.email}`}>{project.email}</Link>
                      {project.email_type && <Tag>{project.email_type}</Tag>}
                    </Space>
                    {project.email_scraped_at && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        抓取时间: {formatDateTime(project.email_scraped_at)}
                      </Text>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">未抓取</Text>
                )}
              </Descriptions.Item>

              {/* 推特 */}
              <Descriptions.Item label="推特">
                {project.twitter_handle ? (
                  <Space direction="vertical" size={0}>
                    <Space>
                      <TwitterOutlined />
                      <Link
                        href={`https://twitter.com/${project.twitter_handle}`}
                        target="_blank"
                      >
                        @{project.twitter_handle}
                      </Link>
                      {project.twitter_dm_open && <Tag color="green">DM开放</Tag>}
                    </Space>
                    {project.twitter_scraped_at && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        抓取时间: {formatDateTime(project.twitter_scraped_at)}
                      </Text>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">未抓取</Text>
                )}
              </Descriptions.Item>

              {/* Telegram群组 */}
              <Descriptions.Item label="Telegram群组">
                {project.telegram_group ? (
                  <Space direction="vertical" size={0}>
                    <Link href={project.telegram_group} target="_blank">
                      {project.telegram_group}
                    </Link>
                    {project.telegram_scraped_at && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        抓取时间: {formatDateTime(project.telegram_scraped_at)}
                      </Text>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">未抓取</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Telegram管理员 */}
          {project.telegram_admins && project.telegram_admins.length > 0 && (
            <Card
              title={`Telegram管理员 (${project.telegram_admins.length})`}
              size="small"
            >
              <Table
                dataSource={project.telegram_admins}
                columns={adminColumns}
                size="small"
                pagination={false}
                rowKey={(record) => record.id || record.username}
              />
            </Card>
          )}

          {/* 抓取状态 */}
          <Card title="抓取状态" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Text strong>邮箱:</Text>
                <Tag
                  color={
                    scrapeStatusConfig[
                      (project.email_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].color
                  }
                  icon={
                    scrapeStatusConfig[
                      (project.email_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].icon
                  }
                >
                  {
                    scrapeStatusConfig[
                      (project.email_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].text
                  }
                </Tag>
              </Space>
              <Space>
                <Text strong>推特:</Text>
                <Tag
                  color={
                    scrapeStatusConfig[
                      (project.twitter_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].color
                  }
                  icon={
                    scrapeStatusConfig[
                      (project.twitter_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].icon
                  }
                >
                  {
                    scrapeStatusConfig[
                      (project.twitter_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].text
                  }
                </Tag>
              </Space>
              <Space>
                <Text strong>Telegram:</Text>
                <Tag
                  color={
                    scrapeStatusConfig[
                      (project.telegram_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].color
                  }
                  icon={
                    scrapeStatusConfig[
                      (project.telegram_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].icon
                  }
                >
                  {
                    scrapeStatusConfig[
                      (project.telegram_scrape_status || 'NOT_SCRAPED') as ScrapeStatus
                    ].text
                  }
                </Tag>
              </Space>
            </Space>
          </Card>
        </Space>
      ) : (
        <Empty description="项目不存在" />
      )}
    </Drawer>
  )
}

export default ProjectInfoDrawer
